import React, { useContext, useEffect, useState } from 'react'
import { BrowserRouter as Router, Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { Flex } from '@chakra-ui/react'
import i18next from 'i18next'
import { AuthContext, AuthProvider } from './AuthProvider'
import Landing from './pages/Landing/Landing'
import { Home } from './pages/Home'
import { Register } from './pages/Register/Register'
import { Login } from './pages/Login/Login'
import { Layout } from './Layout'
import { PrivateRoute } from './PrivateRoute'
import { Reset } from './pages/Reset/Reset'
import { NewPassword } from './pages/NewPassword/NewPassword'
import { Account } from './pages/Account'
import TravelHistoryPage from './pages/TravelHistoryPage/TravelHistoryPage'
import { Wallet } from './pages/Wallet'
import { CustomerSupport } from './pages/CustomerSupport'
import { UserDeleteInfo } from './pages/UserDeleteInfo/UserDeleteInfo'
import { setLanguage } from '@ubirider/pick-component-library'
import { DateTime } from 'luxon'
import { DataProvider } from './context/DataProvider'
import { Vehicles } from './pages/Vehicles/Vehicles'
import { Header } from './pages/Home/components/Header'
import { Tickets } from './pages/Tickets/Tickets'

export const userTimezone = DateTime.local().zoneName

export const getLanguageFromBrowser = language => {
  if (language === 'PT') return navigator.language
  if (language === 'ES') return navigator.language
  return 'BR'
}

function AppContent() {
  const Auth = useContext(AuthContext)
  const location = useLocation()

  const [showHeader, setShowHeader] = useState(false)

  // Determine if the user is authenticated
  const isLogged = Auth.user && Object.keys(Auth.user).length > 0

  useEffect(() => {
    const isRoot = location.pathname === '/'
    setShowHeader(!!isLogged && !isRoot)
  }, [isLogged, location.pathname])

  return (
    <>
      {showHeader && <Header />}
      <Flex
        gap='24px'
        mt={{ base: showHeader ? '70px' : '0', lg: '0' }}
        direction={{ lg: 'row', base: 'column' }}
      >
        <Routes>
          <Route
            element={
              <PrivateRoute>
                <Layout />
              </PrivateRoute>
            }
          >
            <Route path='/home' element={<Home />} />
            <Route path='/travel-history' element={<TravelHistoryPage />} />
            <Route path='/account' element={<Account />} />
            <Route path='/wallet' element={<Wallet />} />
            <Route path='/wallet/success' element={<Wallet />} />
            <Route path='/wallet/fail' element={<Wallet />} />
            <Route path='/vehicles' element={<Vehicles />} />
            <Route path='/tickets' element={<Tickets />} />
            <Route path='/support' element={<CustomerSupport />} />
          </Route>

          <Route element={<Landing />}>
            <Route
              path='/'
              element={isLogged ? <Navigate to='/home' replace /> : <Login />}
            />
            <Route path='/register' element={<Register />} />
            <Route path='/reset' element={<Reset />} />
            <Route path='/change-password' element={<NewPassword />} />
          </Route>

          <Route path='/delete-information' element={<UserDeleteInfo />} />
          <Route path='*' element={<Navigate to='/home' />} />
        </Routes>
      </Flex>
    </>
  )
}

function App() {
  const language = localStorage.getItem('Language')
  const userLang = getLanguageFromBrowser(navigator.language)

  if (language) {
    i18next.changeLanguage(language)
    setLanguage(language === 'BR' ? 'pt' : language.toLowerCase())
  } else {
    localStorage.setItem('Language', userLang)
    i18next.changeLanguage(userLang)
    setLanguage(userLang === 'BR' ? 'pt' : userLang.toLowerCase())
  }

  return (
    <AuthProvider>
      <DataProvider>
        <Router>
          <AppContent />
        </Router>
      </DataProvider>
    </AuthProvider>
  )
}

export default App